import axios from 'axios'
import * as Sentry from '@sentry/react'
import { AUTH_API_PATHS } from '../constants/api.js'

/**
 * Instancia de Axios sin autenticación para servicios de autenticación.
 * Timeout reducido (10s) porque auth debe fallar rápido para no bloquear la UI.
 */
const authAxios = axios.create({
  baseURL: import.meta.env.VITE_AUTH_SERVICE_URL,
  timeout: 10000,
  withCredentials: true,
})

/**
 * Obtiene la sesión compartida desde el UUID
 * @param {string} uuid - UUID de la sesión compartida
 * @returns {Promise} Respuesta con datos de sesión y tokens de Keycloak
 */
export const getSharedSession = async (uuid) => {
  try {
    const response = await authAxios.get(AUTH_API_PATHS.SHARE_SESSION, {
      params: { uuid },
    })

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Sesión compartida obtenida exitosamente',
      level: 'info',
      data: { uuid },
    })

    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'auth', action: 'share-session' },
      contexts: {
        auth: {
          uuid,
          error_message: error.message,
        },
      },
    })
    throw error
  }
}

/**
 * Cierra la sesión en el servidor (limpia cookies y sesión backend)
 * @returns {Promise}
 */
export const logoutSession = async () => {
  try {
    await authAxios.post(AUTH_API_PATHS.LOGOUT)

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Logout en servidor exitoso',
      level: 'info',
    })
  } catch (error) {
    // Si falla el logout en el servidor, lo ignoramos silenciosamente:
    // el estado local se limpia igualmente, y las cookies expirarán solas
    Sentry.captureException(error, {
      tags: { feature: 'auth', action: 'logout' },
    })
  }
}

/**
 * Refresca el access token via el backend (lee la cookie refreshToken HttpOnly)
 * @returns {Promise} Nueva respuesta con tokens actualizados
 */
export const refreshSession = async () => {
  try {
    const response = await authAxios.post(AUTH_API_PATHS.REFRESH)

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Token refrescado exitosamente',
      level: 'info',
    })

    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'auth', action: 'refresh-token' },
      contexts: {
        auth: {
          error_message: error.message,
        },
      },
    })
    throw error
  }
}
